import React, { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography
} from '@mui/material'
import {
  AddRounded, DeleteRounded, EditRounded, NetworkCheckRounded, PersonAddRounded, PrintRounded, StarBorderRounded, StarRounded
} from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useUser } from '../../generic/hooks/useUser'
import useLabelPrintFlow from '../../generic/qr/labelPrinting/useLabelPrintFlow'
import LabelPrintFeedback from '../../generic/qr/labelPrinting/LabelPrintFeedback'
import AlertUI from '../../generic/AlertUI'
import AssignDefaultPrinterDialog from './AssignDefaultPrinterDialog'
import PrinterFormDialog from './PrinterFormDialog'
import PrinterStatusChip from '../../generic/qr/labelPrinting/PrinterStatusChip'
import { checkPrinter, deletePrinter, getDefaultPrinter, listPrinters, outcomeOf, setDefaultPrinter, testPrintPrinter } from '../../generic/qr/labelPrinting/labelPrintingApi'
import {
  AVAILABILITY_TEXT, LAYOUT_TEXT, OUTCOME_TEXT, PRINT_METHOD_TEXT, PROBLEM_TEXT, effectivePrintWidth, labelSizeText, localDateTimeText, textOf
} from '../../generic/qr/labelPrinting/labelPrintingTexts'

// E24: the same gap below a notice as between the toolbar and the table.
const NOTICE_SPACING = { sx: { mb: 2 } }

// Grouped so the table fits a laptop (~1280-1400 px) without horizontal scroll (PO, 2026-10-28): address = host:port,
// label = size · dpi with layout · method below, model with the reported one below. The actions column stays fixed
// on the right in case a narrower screen still scrolls.
const COLUMNS = ['Name', 'Address', 'Model', 'Label', 'Print width', 'Active', 'Status', 'Last check']

const STICKY_ACTIONS_SX = { position: 'sticky', right: 0, zIndex: 1, bgcolor: 'background.paper', whiteSpace: 'nowrap' }
// The app's layout grows with the table's MINIMUM width (a Stack with min-width: min-content), so a long host name
// (zebra-expedicion-linea3.logoplaste.local) may break anywhere — wide enough for an IP:port on one line — and the
// actions stack in two lines.
const ADDRESS_SX = { overflowWrap: 'anywhere', minWidth: '8.5rem' }
// Half of MUI's 16 px side padding per cell: nine columns give back ~140 px on a 1280 px laptop.
const TABLE_SX = { '& th, & td': { px: 1 } }
const ACTIONS_SX = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }
const SECONDARY_SX = { fontSize: 12, color: 'text.secondary' }

// Historia 226 (S30-S41, CU1-CU5): the "Printers" tab of Configuration → Printers.
const PrinterList = () => {
  const { t_ } = useSmartTranslation()
  const { user } = useUser()
  const isAdmin = Boolean(user?.profile?.code === 'ADMIN' || user?.profile?.code === 'SUPERADMIN')

  const [printers, setPrinters] = useState([])
  const [defaultId, setDefaultId] = useState('')
  const [editing, setEditing] = useState(false)
  const [editedPrinter, setEditedPrinter] = useState({})
  const [deleting, setDeleting] = useState({})
  const [checkingIds, setCheckingIds] = useState([])
  const testPrintFlow = useLabelPrintFlow()
  const [assigningPrinter, setAssigningPrinter] = useState(null)
  // E24: the notices of this screen are MIC's AlertUI, at the top of the screen like in the rest of MIC.
  const [AlertElement, showAlert] = AlertUI(NOTICE_SPACING)
  const notify = useCallback((severity, text) => showAlert({ severity, title: text, message: ' ' }), [showAlert])

  const load = useCallback(async () => {
    try {
      setPrinters(await listPrinters())
    } catch (error) {
      notify('error', t_('Unexpected error'))
    }
    try {
      setDefaultId((await getDefaultPrinter())?.printerId || '')
    } catch (error) {
      setDefaultId('')
    }
  }, [t_, notify])

  // E8: one default printer per user; the star moves to the chosen one.
  const makeDefault = async (printer) => {
    try {
      await setDefaultPrinter(printer.id)
      setDefaultId(printer.id)
    } catch (error) {
      notify('error', t_(textOf(OUTCOME_TEXT, error?.data?.outcome) || 'Unexpected error'))
    }
  }

  // E13/E21: ADMIN/SUPERADMIN assign the printer as the default of several users (see AssignDefaultPrinterDialog).
  const openAssignDefault = (printer) => setAssigningPrinter(printer)
  // E23: closing the dialog (done or not) reloads the list so each row's "N users" is up to date.
  const handleAssignDone = (text) => {
    setAssigningPrinter(null)
    notify('success', text)
    load()
  }
  const handleAssignClose = () => {
    setAssigningPrinter(null)
    load()
  }

  useEffect(() => { load() }, [load])

  const openForm = (printer) => {
    setEditedPrinter(printer)
    setEditing(true)
  }

  const printWidthText = (printer) => {
    const { width, reported } = effectivePrintWidth(printer)
    return reported ? `${width} mm ${t_('(reported)')}` : `${width} mm`
  }

  const savedNotice = (printer) => (printer.availability === 'AVAILABLE'
    ? { severity: 'success', text: t_('Printer saved: available') }
    : {
        severity: 'warning',
        text: t_('Printer saved: unavailable ({{reason}})', {
          reason: t_(printer.availability === 'UNAVAILABLE'
            ? textOf(OUTCOME_TEXT, printer.availabilityReason)
            : textOf(AVAILABILITY_TEXT, printer.availability))
        })
      })

  const handleSaved = (printer) => {
    setEditing(false)
    const { severity, text } = savedNotice(printer)
    notify(severity, text)
    load()
  }

  const confirmDelete = async () => {
    const { id } = deleting
    setDeleting({})
    try {
      await deletePrinter(id)
    } catch (error) {
      // PRINTER_NOT_FOUND (S40): another session deleted it already; the reload below shows it gone.
      if (outcomeOf(error) !== 'PRINTER_NOT_FOUND') notify('error', t_('Unexpected error'))
    }
    load()
  }

  // E24: "Check" always says how it went; unavailable stays until closed, with the reason and the printer's problems.
  const notifyChecked = (printer) => {
    if (printer.availability === 'AVAILABLE') {
      showAlert({ severity: 'success', title: t_('Printer available'), message: ' ' })
      return
    }
    const reason = t_(printer.availability === 'UNAVAILABLE'
      ? textOf(OUTCOME_TEXT, printer.availabilityReason)
      : textOf(AVAILABILITY_TEXT, printer.availability))
    const problems = (printer.availabilityProblems || []).map((problem) => t_(textOf(PROBLEM_TEXT, problem)))
    showAlert({
      severity: 'error',
      title: t_('Printer unavailable: {{reason}}', { reason }),
      message: problems.length > 0 ? <>{problems.map((problem) => <Box key={problem}>{problem}</Box>)}</> : ' '
    })
  }

  const check = async (printer) => {
    setCheckingIds((ids) => [...ids, printer.id])
    try {
      const checked = await checkPrinter(printer.id)
      // The check answer always carries assignedUserCount 0 (Sun computes it only in the list, E23): keep the row's own.
      setPrinters((current) => current.map((candidate) => (candidate.id === checked.id ? { ...checked, assignedUserCount: candidate.assignedUserCount } : candidate)))
      notifyChecked(checked)
    } catch (error) {
      notify('error', t_('Unexpected error'))
      load()
    } finally {
      setCheckingIds((ids) => ids.filter((id) => id !== printer.id))
    }
  }

  // E12: a test label on this printer; the answer is treated like a print (same notices, Retry repeats the test).
  const testPrint = (printer) => {
    const send = () => testPrintPrinter(printer.id, { label: t_('TEST') })
    testPrintFlow.attempt(send, printer.name, false, { printerId: printer.id, retrySend: send })
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      {AlertElement}
      <LabelPrintFeedback {...testPrintFlow} />
      {/* Toolbar outside the scrolling area: "New printer" is always in view (PO, 2026-10-28) */}
      <Box data-testid='printers-toolbar' sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Button variant='contained' startIcon={<AddRounded />} onClick={() => openForm({})}>
          {t_('New printer')}
        </Button>
      </Box>
      <TableContainer data-testid='printers-table' sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table size='small' sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => <TableCell key={column}>{t_(column)}</TableCell>)}
              <TableCell sx={STICKY_ACTIONS_SX}>{t_('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {printers.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align='center'>
                  {t_('There are no printers. Add the first one.')}
                </TableCell>
              </TableRow>
            )}
            {printers.map((printer) => {
              const checking = checkingIds.includes(printer.id)
              return (
                <TableRow key={printer.id}>
                  <TableCell>
                    {isAdmin
                      ? (
                        <>
                          <Tooltip title={t_('Assign to users')}>
                            <IconButton aria-label={t_('Assign to users')} size='small' onClick={() => openAssignDefault(printer)}>
                              <PersonAddRounded fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          {printer.assignedUserCount > 0 && (
                            <Typography component='span' data-testid='assigned-count' sx={{ fontSize: 12, color: 'text.secondary', mr: 0.5 }}>
                              {printer.assignedUserCount === 1 ? t_('1 user') : t_('{{count}} users', { count: printer.assignedUserCount })}
                            </Typography>
                          )}
                        </>
                        )
                      : (
                        <Tooltip title={t_('Use as default')}>
                          <IconButton aria-label={t_('Use as default')} aria-pressed={printer.id === defaultId} size='small' onClick={() => makeDefault(printer)}>
                            {printer.id === defaultId ? <StarRounded fontSize='small' color='warning' /> : <StarBorderRounded fontSize='small' />}
                          </IconButton>
                        </Tooltip>
                        )}
                    {printer.name}
                  </TableCell>
                  <TableCell sx={ADDRESS_SX}>{`${printer.host}:${printer.port}`}</TableCell>
                  <TableCell>
                    <div>{printer.model}</div>
                    {printer.reportedModel && (
                      <Typography sx={SECONDARY_SX}>{`${t_('Reported model')}: ${printer.reportedModel}`}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>{`${labelSizeText(printer)} · ${printer.dpi} ${t_('dpi')}`}</div>
                    <Typography sx={SECONDARY_SX}>
                      {`${t_(textOf(LAYOUT_TEXT, printer.layout))} · ${t_(textOf(PRINT_METHOD_TEXT, printer.printMethod))}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{printWidthText(printer)}</TableCell>
                  <TableCell>
                    <Checkbox checked={printer.active} disabled size='small' inputProps={{ 'aria-label': t_('Active') }} />
                  </TableCell>
                  <TableCell>
                    <PrinterStatusChip
                      availability={printer.availability}
                      availabilityReason={printer.availabilityReason || ''}
                      availabilityProblems={printer.availabilityProblems || []}
                    />
                  </TableCell>
                  <TableCell>{localDateTimeText(printer.lastCheckAt)}</TableCell>
                  <TableCell sx={STICKY_ACTIONS_SX}>
                    <Box sx={ACTIONS_SX}>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => check(printer)}
                        disabled={checking}
                        startIcon={checking ? <CircularProgress size={14} /> : <NetworkCheckRounded fontSize='small' />}
                      >
                        {t_('Check')}
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => testPrint(printer)}
                        disabled={testPrintFlow.busy}
                        startIcon={testPrintFlow.busy ? <CircularProgress size={14} /> : <PrintRounded fontSize='small' />}
                      >
                        {t_('Test print')}
                      </Button>
                      <Box>
                        <Tooltip title={t_('Edit')}>
                          <IconButton aria-label={t_('Edit')} onClick={() => openForm(printer)} size='small'>
                            <EditRounded fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t_('Delete')}>
                          <IconButton aria-label={t_('Delete')} onClick={() => setDeleting(printer)} size='small'>
                            <DeleteRounded fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {editing && (
        <PrinterFormDialog printer={editedPrinter} printers={printers} onClose={() => setEditing(false)} onSaved={handleSaved} />
      )}

      <Dialog open={Boolean(deleting.id)} onClose={() => setDeleting({})}>
        <DialogTitle>{t_('Delete')}</DialogTitle>
        <DialogContent>
          {t_('Delete the printer {{name}}? Its print history is kept.', { name: deleting.name })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting({})}>{t_('Cancel')}</Button>
          <Button color='error' variant='contained' onClick={confirmDelete}>{t_('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {assigningPrinter && (
        <AssignDefaultPrinterDialog printer={assigningPrinter} printers={printers} onClose={handleAssignClose} onDone={handleAssignDone} />
      )}

    </Box>
  )
}

export default PrinterList
